import React, { MouseEvent, ReactNode } from 'react';
import Breadcrumb from '../../Breadcrumb';
import CustomTabv2, { CustomTabItem } from '../../custom/CustomTabv2';
import BasicEditorComponent from './BasicEditorComponent';
import BasicAccessComponent from './BasicAccessComponent';
import { FormicaProps, withFormica } from 'helpers/withFormica';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CollectionNames from '../../../constant/CollectionNames';
import { changeBasic, changeBasicDependencies, changeBasicSchema, changeBasicVariableDirty, changeIsBasicUpdate, clearBasic } from '../../../redux/actions/BasicActions';
import CustomLoading from '../../custom/CustomLoading';
import { ConfirmPopup } from 'primereact/confirmpopup';
import ComponentName from '../../../constant/ComponentName';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import BasicVariableService from '../../../service/BasicVariableService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../deploy/UndeployImpactDialog';
import { BasicVariable } from '../../../domain/enrichment/EnrichmentDomain';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import { clearPage } from 'redux/actions/PageActions';
import { BasicVariableSchema } from '../../../domain/enrichment/EnrichmentSchemaDomains';
import { ComponentDependency } from '../../../domain/ui/ComponentMetadata';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import ComponentErrorLog from '../../ComponentErrorLog';
import { BasicState } from '../../../redux/reducers/BasicReducer';
import DataType from 'constant/DataType';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import RecordNotFound from '../../../pages/RecordNotFound';
import eq = query.eq;
import and = query.and;
import QueryBuilder = query.QueryBuilder;

class BasicEditorContainer extends StandardEditorContainerComponent<Props, State, BasicVariable> {
  public state = new State();

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly basicVariableService: BasicVariableService = ServiceContainer.getInstance().get(ServiceNames.BASIC_VARIABLE);

  private computation: subscribe.Computation | undefined;
  private dependencySubscription: subscribe.Subscription | undefined;
  private structSubscription: subscribe.Subscription | undefined;
  private basicVariableSubscription: subscribe.Subscription | undefined;
  private basicVariableSchemaSubscription: subscribe.Subscription | undefined;
  private initialDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private syncDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private isInitialDirtyResetScheduled: boolean = false;

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.ENRICHMENT,
      icon: 'enrichment',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const { loading } = this.state;
    const {
      lastCategory,
      basicVariable: { basicVariable, schema, isDirty },
      isUpdate,
    } = this.props;

    if (loading) {
      return <CustomLoading />;
    }
    if (this.props.router.params.id && basicVariable.name == '') {
      return <RecordNotFound itemId={this.props.router.params.id} component={ComponentName.BASIC_VARIABLE} />;
    }

    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={basicVariable.name}
          description={basicVariable.description}
          status={schema?.stage}
          component={ComponentName.BASIC_VARIABLE}
          isUpdate={isUpdate}
          icon={'enrichment'}
          onChangeName={this.onChangeName}
          onChangeDescription={this.onChangeDescription}
          onSave={this.saveVariable}
          onDeploy={this.deployVariable}
          onUndeploy={this.undeployVariable}
          onRemove={this.removeVariable}
          disableSave={!RoleHelper.canEdit('EnrichmentBasic')}
          disableRemove={!RoleHelper.canEdit('EnrichmentBasic')}
          disableNameChange={!RoleHelper.canEdit('EnrichmentBasic')}
          disableDescriptionChange={!RoleHelper.canEdit('EnrichmentBasic')}
          disableDeploy={!RoleHelper.canDeploy('EnrichmentBasic') || isDirty}
          disableUndeploy={!RoleHelper.canDeploy('EnrichmentBasic')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearBasic } = this.props;
    if (this.initialDirtyResetTimer) {
      clearTimeout(this.initialDirtyResetTimer);
    }
    if (this.syncDirtyResetTimer) {
      clearTimeout(this.syncDirtyResetTimer);
    }
    clearBasic();
    super.componentWillUnmount();
  }

  public componentDidUpdate(prevProps: Props) {
    const {
      basicVariable: { isUpdate },
      changeBasicVariableDirty,
    } = this.props;
    if (!prevProps.basicVariable.isUpdate && isUpdate && !this.isInitialDirtyResetScheduled) {
      this.isInitialDirtyResetScheduled = true;
      this.initialDirtyResetTimer = setTimeout(() => {
        changeBasicVariableDirty(false);
      }, 1200);
    }
  }

  protected _startTracker = async () => {
    const variableId = this.getVariableIdFromRoute();
    if (variableId == undefined) {
      this.setState({ loading: false });
    }
    const basicVariableQuery = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('name', variableId));
    const basicVariableSchemaQuery = query.QueryBuilder.select().from(CollectionNames.ENRICHMENT_MODELS).where(eq('id', variableId));
    this.basicVariableSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, basicVariableQuery.build());
    this.basicVariableSchemaSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENT_MODELS, basicVariableSchemaQuery.build());
    this.structSubscription = subscribe.Formica.subscribe(CollectionNames.DATA_STRUCTURES, QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.basicVariableSubscription, this.basicVariableSchemaSubscription, this.structSubscription]);
    if (variableId != undefined) {
      const dependencyQuery = query.QueryBuilder.select()
        .from(CollectionNames.COMPONENT_DEPENDENCY)
        .where(and(eq('dependentId', variableId), eq('dependentType', 'BasicVariable')))
        .build();
      this.dependencySubscription = subscribe.Formica.subscribe(CollectionNames.COMPONENT_DEPENDENCY, dependencyQuery);
      this.computation.addSubscription(this.dependencySubscription);
    }
  };

  protected tracker = async () => {
    const { changeBasic, changeIsBasicUpdate, changeBasicSchema, changeBasicDependencies, changeBasicVariableDirty } = this.props;
    if (
      this.basicVariableSubscription?.ready() &&
      this.structSubscription?.ready() &&
      (this.dependencySubscription == undefined || this.dependencySubscription?.ready())
    ) {
      const variableId = this.getVariableIdFromRoute();
      if (variableId == undefined) {
        changeIsBasicUpdate(false);
        changeBasicVariableDirty(false);
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        return;
      }
      const basicVariableQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('id', variableId)).build();
      const dependencyQuery = query.QueryBuilder.select()
        .from(CollectionNames.COMPONENT_DEPENDENCY)
        .where(and(eq('dependentId', variableId), eq('dependentType', 'BasicVariable')))
        .build();
      const dependencies = await this.repository.findMany<ComponentDependency>(dependencyQuery);
      changeBasicDependencies(dependencies);
      const basicVariableSchemaQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ENRICHMENT_MODELS).where(eq('id', variableId)).build();
      const basicVariable: BasicVariable | undefined = await this.repository.find(basicVariableQuery);
      if (!basicVariable) {
        return;
      }
      this.castValue(basicVariable);
      changeBasic(basicVariable);
      changeBasicVariableDirty(false);
      if (this.syncDirtyResetTimer) {
        clearTimeout(this.syncDirtyResetTimer);
      }
      this.syncDirtyResetTimer = setTimeout(() => {
        changeBasicVariableDirty(false);
      }, 1200);
      const basicVariableSchema: BasicVariableSchema | any = await this.repository.find(basicVariableSchemaQuery);
      changeBasicSchema(basicVariableSchema);
      RoleHelper.setCurrentAccess(basicVariable);
      this.setState({ loading: false });
    }
  };

  protected _stopTracker = async () => {
    await this.basicVariableSubscription?.stop();
    await this.basicVariableSchemaSubscription?.stop();
    await this.dependencySubscription?.stop();
    await this.structSubscription?.stop();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private getVariableIdFromRoute = () => {
    const { changeIsBasicUpdate } = this.props;
    const id = this.props.router.params.id;
    changeIsBasicUpdate(id != undefined);
    return id;
  };

  private onChangeName = (name: string) => {
    const {
      basicVariable: { basicVariable },
      changeBasic,
    } = this.props;
    if (isStringValid(name)) {
      basicVariable.name = name.trim();
      changeBasic(basicVariable);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      basicVariable: { basicVariable },
      changeBasic,
    } = this.props;
    basicVariable.description = description;
    changeBasic(basicVariable);
  };

  private switchToUpdate = async () => {
    const {
      router: { navigate, params },
      enterComponent,
      basicVariable: { basicVariable },
    } = this.props;
    if (basicVariable.name == '') return;
    enterComponent({ component: ComponentName.ENRICHMENT, id: basicVariable.name });
    navigate(`/enrichment${params.folderId ? `/folder/${params.folderId}` : ''}/basic/${basicVariable.name}`, { replace: true });
    await this.resubscribeTracker();
  };

  private saveVariable = async () => {
    const {
      basicVariable: { basicVariable },
      isUpdate,
      lastCategory,
      changeIsBasicUpdate,
    } = this.props;
    try {
      const savePayload = { ...basicVariable };
      savePayload.value = JSON.stringify(savePayload.value);
      const isSaved = await this.basicVariableService.save(basicVariable, isUpdate, lastCategory);
      if (isSaved) {
        changeIsBasicUpdate(true);
        if (!isUpdate) await this.switchToUpdate();
      }
    } catch (e) {
      console.error(e);
    }
  };

  private undeployVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      basicVariable: { basicVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => confirmUndeployWithImpact(ComponentName.BASIC_VARIABLE, [basicVariable.name], () => this.basicVariableService.undeploy(basicVariable.name)),
      message: LanguageUtil.get({ id: 'messages.confirm.undeployVariable', defaultMessage: 'Are you sure to undeploy this basic variable ?' }),
    });
  };

  private deployVariable = (e: MouseEvent<HTMLElement>) => {
    const {
      basicVariable: { basicVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.basicVariableService.save(basicVariable, true, {} as EnrichedComponentCategory);
        await this.basicVariableService.deploy(basicVariable);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deployVariable', defaultMessage: 'Are you sure to deploy this basic variable ?' }),
    });
  };

  private removeVariable = (e: MouseEvent<HTMLElement>) => {
    const {
      basicVariable: { basicVariable },
    } = this.props;
    const {
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        try {
          const success = await this.basicVariableService.delete(basicVariable.name);
          if (success) {
            navigate(`/enrichment`);
          }
        } catch (e) {
          console.log(e);
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeVariable', defaultMessage: 'Are you sure to remove this basic variable?' }),
    });
  };

  protected getTabs(): CustomTabItem[] {
    return [
      {
        key: 'Editor',
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Key' }),
        content: <BasicEditorComponent />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <BasicAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.BASIC_VARIABLE, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.BASIC_VARIABLE} documentName={this.props.basicVariable.basicVariable.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.BASIC_VARIABLE, true) && this.environmentUtil.isAuditLogVisible(),
      },
      {
        key: 'Error',
        label: LanguageUtil.get({ id: 'common.tabs.error', defaultMessage: 'Error' }),
        content: <ComponentErrorLog component={ComponentName.BASIC_VARIABLE} documentName={this.props.basicVariable.basicVariable.name} />,
        visible: RoleHelper.canManage(ComponentName.BASIC_VARIABLE, true),
      },
    ];
  }

  protected changeItem = (item: BasicVariable) => {
    const { changeBasic } = this.props;
    changeBasic(item);
  };

  private castValue(basicVariable: BasicVariable) {
    const type = basicVariable.properties.dataType;
    if (basicVariable.value == undefined) return;
    switch (type) {
      case DataType.NUMBER:
        return Number(basicVariable.value);
      case DataType.STRING:
        return String(basicVariable.value);
      case DataType.ARRAY:
      case DataType.OBJECT:
        return JSON.parse(basicVariable.value);
      case DataType.BOOLEAN:
        return basicVariable.value == 'true';
      default:
        throw new Error('Unknown type ' + type);
    }
  }
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  basicVariable: state.basic as BasicState,
  isUpdate: state.basic?.isUpdate,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = {
  changeIsBasicUpdate,
  changeBasic,
  changeBasicSchema,
  changeBasicDependencies,
  changeBasicVariableDirty,
  clearBasic,
  enterComponent,
  leaveComponent,
  clearPage,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(BasicEditorContainer));
